/**
 * Unified access-control hook.
 *
 * Hierarchy: tier > permission > feature gate > super user.
 * Only checks dimensions you pass in — omitted keys are skipped.
 * Super user is the last fallback — it does not bypass earlier checks.
 * Returns the first denial reason so callers can show the right UI.
 */

import { useSelector } from 'react-redux'

import { type RootState } from '~redux/rootReducer'
import {
  type AccessAction,
  type AccessTier,
  AccessTierEnum,
  type GeneralResource,
  type IdentityId,
} from '~types'

import { useFeatureGate } from './useFeatureGate'
import { usePermission } from './usePermission'
import { useTierAccess } from './useTierAccess'

type StatsigCustomAttributes = Record<string, string | number | boolean | Array<string> | undefined>

export const ACCESS_DENIED_REASON = {
  FEATURE_DISABLED: 'feature_disabled',
  NO_PERMISSION: 'no_permission',
  TIER_INSUFFICIENT: 'tier_insufficient',
} as const
export type AccessDeniedReason = (typeof ACCESS_DENIED_REASON)[keyof typeof ACCESS_DENIED_REASON]

export type UseAccessOptions = {
  feature?: string
  featureGateOptions?: { custom?: StatsigCustomAttributes }
  permission?: { resource: GeneralResource; action: AccessAction }
  targetOrganizationIdentityId?: IdentityId
  tier?: AccessTier
}

export type UseAccessResult = {
  allowed: boolean
  allowedForIdentities: IdentityId[]
  loading: boolean
  reason: AccessDeniedReason | null
  isSuperUser: boolean
}

export const useAccess = (options: UseAccessOptions): UseAccessResult => {
  const { feature, featureGateOptions, permission, targetOrganizationIdentityId, tier } = options

  const isSuperUser = Boolean(useSelector((state: RootState) => state?.auth.user?.super))

  // Always call each hook (rules of hooks), but ignore results when that dimension isn't requested.
  const { enabled: featureEnabled, loading: featureLoading } = useFeatureGate(
    feature ?? '',
    featureGateOptions,
  )
  const featureAllowed = feature ? featureEnabled : true
  const featureIsLoading = feature ? featureLoading : false

  const checkPermissions = permission?.resource && permission?.action
  const { allowedIdentities, loading: permissionLoading } = usePermission(
    permission?.resource ?? null,
    permission?.action ?? null,
  )

  const permissionResult =
    !checkPermissions ||
    (targetOrganizationIdentityId == null
      ? allowedIdentities.length > 0
      : allowedIdentities.includes(targetOrganizationIdentityId))
  const permissionIsLoading = permission ? permissionLoading : false

  const { allowed: tierAllowed, loading: tierLoading } = useTierAccess(
    tier ?? AccessTierEnum.Enum.ActivateAccess,
  )
  const tierResult = tier ? tierAllowed : true
  const tierIsLoading = tier ? tierLoading : false

  const loading = featureIsLoading || permissionIsLoading || tierIsLoading

  if (!tierResult) {
    return {
      allowed: false,
      allowedForIdentities: allowedIdentities,
      loading,
      reason: ACCESS_DENIED_REASON.TIER_INSUFFICIENT,
      isSuperUser,
    }
  }

  if (checkPermissions && !permissionResult) {
    return {
      allowed: false,
      allowedForIdentities: allowedIdentities,
      loading,
      reason: ACCESS_DENIED_REASON.NO_PERMISSION,
      isSuperUser,
    }
  }

  if (!featureAllowed) {
    return {
      allowed: false,
      allowedForIdentities: allowedIdentities,
      loading,
      reason: ACCESS_DENIED_REASON.FEATURE_DISABLED,
      isSuperUser,
    }
  }

  // Super user is the last fallback — all earlier checks must pass first.
  if (isSuperUser) {
    return {
      allowed: true,
      allowedForIdentities: allowedIdentities,
      loading: false,
      reason: null,
      isSuperUser,
    }
  }

  return {
    allowed: true,
    allowedForIdentities: allowedIdentities,
    loading,
    reason: null,
    isSuperUser,
  }
}
